import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Stack,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";

const baseUrl = process.env.REACT_APP_API_PATH;

const InterviewApprovals = () => {
  const [data, setData] = useState([]);
  const { userResult } = useSelector((state) => state.Auth);
  const [loading, setLoading] = useState(false);

  // Reject dialog state
  const [openReject, setOpenReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const page = 0;
  const size = 10;
  const vendorId = userResult?.vendorid;

  useEffect(() => {
    fetchInterviewApprovals();
  }, [vendorId]);

  const fetchInterviewApprovals = async () => {
    if (!vendorId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${baseUrl}/ebench/vendor/interview-availability`,
        { params: { vendorId, page, size } }
      );

      setData(response.data?.data?.content || []);
    } catch (error) {
      console.error("Failed to fetch interview approvals", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    if (status === "ACCEPTED") return "APPROVED";
    if (status === "REJECTED") return "REJECTED";
    return "PENDING";
  };

  /* =======================
     APPROVE
     ======================= */
  const handleApprove = async (interviewId) => {
    try {
      setActionLoading(true);
      await axios.post(
        `${baseUrl}/ebench/vendor/interview/${interviewId}/accept`
      );
      fetchInterviewApprovals();
    } catch (error) {
      console.error("Approve failed", error);
    } finally {
      setActionLoading(false);
    }
  };

  /* =======================
     REJECT
     ======================= */
  const openRejectDialog = (interviewId) => {
    setSelectedInterviewId(interviewId);
    setRejectReason("");
    setOpenReject(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) return;

    try {
      setActionLoading(true);
      await axios.post(`${baseUrl}/ebench/vendor/interview/reject`, {
        interviewId: selectedInterviewId,
        reason: rejectReason,
      });

      setOpenReject(false);
      fetchInterviewApprovals();
    } catch (error) {
      console.error("Reject failed", error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Interview Approvals
      </Typography>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Job Id</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Candidate</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Job Title</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Interview Date & Time</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Interview Mode</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Action</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}

                {!loading && data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No interview approvals found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  data.map((row) => {
                    const status = getStatusLabel(row.acceptanceStatus);

                    return (
                      <TableRow key={row.interviewId}>
                        <TableCell>{row.jobId}</TableCell>
                        <TableCell>{row.candidateName}</TableCell>
                        <TableCell>{row.jobTitle}</TableCell>
                        <TableCell>
                          {new Date(row.interviewDate).toLocaleString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </TableCell>
                        <TableCell>{row.interviewMode}</TableCell>
                        <TableCell>
                          <Chip
                            label={status}
                            size="small"
                            color={
                              status === "APPROVED"
                                ? "success"
                                : status === "REJECTED"
                                ? "error"
                                : "warning"
                            }
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {status === "PENDING" ? (
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={actionLoading}
                                onClick={() => handleApprove(row.interviewId)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={actionLoading}
                                onClick={() =>
                                  openRejectDialog(row.interviewId)
                                }
                              >
                                Reject
                              </Button>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* =======================
          REJECT DIALOG
          ======================= */}
      <Dialog
        open={openReject}
        onClose={() => setOpenReject(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Interview Availability</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReject(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={!rejectReason.trim() || actionLoading}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterviewApprovals;
